/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Car,
  CheckCircle2,
  AlertCircle,
  Loader2,
  CalendarClock,
  Copy,
  Clock3,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_API = `${BACKEND_URL}/api/public`;

function money(n) {
  return Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function CopyRow({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-gray-50 border border-gray-200">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-gray-500">
          {label}
        </div>
        <div className="text-sm font-mono truncate" data-testid={`${testId}-value`}>
          {value}
        </div>
      </div>
      <button
        onClick={copy}
        data-testid={`${testId}-copy`}
        className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-300 hover:border-gray-900 text-xs transition"
      >
        <Copy size={11} /> {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export default function PublicRenew() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [method, setMethod] = useState("zelle");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios
      .get(`${PUBLIC_API}/renewal/${token}`)
      .then((r) => {
        setInfo(r.data);
        if (r.data?.renewal_pending) setSubmitted(true);
        // Default the method radio to whichever handle actually exists
        if (!r.data?.zelle_handle && r.data?.cashapp_handle) setMethod("cashapp");
        else if (!r.data?.zelle_handle && !r.data?.cashapp_handle) setMethod("cash");
      })
      .catch((e) =>
        setError(e?.response?.data?.detail || "Invalid or expired renewal link.")
      );
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${PUBLIC_API}/renewal/${token}/request`, {
        method,
        note: note.trim(),
      });
      setSubmitted(true);
    } catch (e) {
      setError(
        e?.response?.data?.detail || "Could not submit your renewal request. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (error && !info) {
    return (
      <div className="min-h-screen bg-gray-50 grid place-items-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="text-red-500 mx-auto mb-3" size={48} />
            <h1 className="font-heading text-2xl font-bold mb-2">Link error</h1>
            <p className="text-sm text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-gray-50 grid place-items-center p-4">
        <Loader2 className="animate-spin text-gray-500" size={28} />
      </div>
    );
  }

  const hasZelle = !!info.zelle_handle;
  const hasCashApp = !!info.cashapp_handle;

  // Success / already submitted state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 grid place-items-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="text-green-500 mx-auto mb-3" size={48} />
            <h1 className="font-heading text-2xl font-bold mb-1">
              Renewal request received
            </h1>
            <p className="text-sm text-gray-600 mb-4">
              We got your request for another week on your {info.vehicle_label}. As soon as {info.business_name} confirms your payment, your return date will be extended by <strong>7 days</strong> and you'll get a text.
            </p>
            <div className="inline-flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
              <Clock3 size={12} /> Awaiting staff confirmation
            </div>
            {info.business_phone && (
              <p className="text-xs text-gray-500 mt-6">
                Questions? Call {info.business_name} at{" "}
                <a href={`tel:${info.business_phone}`} className="underline">
                  {info.business_phone}
                </a>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-6">
          <Car className="text-gray-900" size={20} />
          <span className="font-heading text-lg font-bold text-gray-900">
            {info.business_name}
          </span>
        </div>

        <Card className="mb-4">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">
              Renewal
            </div>
            <h1 className="font-heading text-2xl font-bold mb-1">
              Extend your rental by 1 week
            </h1>
            <p className="text-sm text-gray-600 mb-5">
              Send your weekly payment using one of the options below, then tap “I've sent the payment” — {info.business_name} will confirm and extend your return date.
            </p>

            <div className="grid grid-cols-1 gap-3 mb-5">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">
                  Renter
                </div>
                <div className="text-sm font-medium">{info.customer_name || "—"}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">
                  Vehicle
                </div>
                <div className="text-sm font-medium">
                  {info.vehicle_label || "—"}
                  {info.plate && <span className="text-gray-500"> · {info.plate}</span>}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 flex items-center gap-1">
                  <CalendarClock size={11} /> Current return date
                </div>
                <div className="text-sm font-medium">
                  {fmtDateTime(info.current_return)}
                </div>
              </div>
              <div className="mt-2 pt-3 border-t border-gray-100">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">
                  Amount to send
                </div>
                <div
                  className="font-heading text-3xl font-bold tabular-nums text-gray-900"
                  data-testid="renew-amount"
                >
                  {money(info.amount)}
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-5">
              {hasZelle && (
                <CopyRow
                  label="Zelle"
                  value={info.zelle_handle}
                  testId="renew-zelle"
                />
              )}
              {hasCashApp && (
                <CopyRow
                  label="Cash App"
                  value={info.cashapp_handle}
                  testId="renew-cashapp"
                />
              )}
              {info.payment_instructions && (
                <div className="text-xs text-gray-600 border border-dashed border-gray-300 rounded-md px-3 py-2 whitespace-pre-line">
                  {info.payment_instructions}
                </div>
              )}
              {!hasZelle && !hasCashApp && !info.payment_instructions && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Payment instructions haven't been configured. Please call {info.business_name}
                  {info.business_phone ? ` at ${info.business_phone}` : ""} to arrange
                  payment.
                </div>
              )}
            </div>

            <div className="mb-4">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
                I paid via
              </div>
              <div className="grid grid-cols-3 gap-2" data-testid="renew-method-group">
                {[
                  { id: "zelle", label: "Zelle", show: hasZelle },
                  { id: "cashapp", label: "Cash App", show: hasCashApp },
                  { id: "cash", label: "Cash", show: true },
                ]
                  .filter((m) => m.show)
                  .map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setMethod(m.id)}
                      data-testid={`renew-method-${m.id}`}
                      className={`px-3 py-2 rounded-md border text-sm font-medium transition ${
                        method === m.id
                          ? "border-gray-900 bg-gray-900 text-white"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-500"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
              </div>
            </div>

            <div className="mb-5">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">
                Note for staff (optional)
              </div>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. sent from John S. at 3pm"
                className="text-sm"
                data-testid="renew-note"
                rows={2}
              />
            </div>

            {error && (
              <div className="text-xs text-red-600 mb-2" data-testid="renew-error">
                {error}
              </div>
            )}

            <Button
              onClick={submit}
              disabled={submitting}
              data-testid="renew-submit-btn"
              className="w-full bg-black hover:bg-gray-800 text-white h-12 text-base"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} /> Submitting…
                </>
              ) : (
                <>I've sent the payment</>
              )}
            </Button>

            <p className="text-[11px] text-gray-500 mt-3 text-center">
              After you tap, staff will confirm on their end and your return date will be extended <strong>+7 days</strong>.
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-[11px] text-gray-500">
          Need help? Call {info.business_name}
          {info.business_phone ? ` at ${info.business_phone}` : ""}.
        </p>
      </div>
    </div>
  );
}
